import { ColumnsType } from 'antd/lib/table';
import { useIntl } from 'react-intl';
import { useLastPartsPurchases } from '@/core/domain/purchase/Purchase.hooks';
import { quantityFormatter } from '@/utils/number';
import { getTableScrollX } from '@/utils/table';
import { Table } from '@/components/_common';
import { Card, Divider } from 'antd';
import {
  LastItemPurchases,
  LastItemPurchasesOrderColumn,
  LastItemPurchasesRequest,
} from '@/core/domain/purchase/Purchase.types';
import moment from 'moment';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { useDebounce } from '@/hooks';
import { useState } from 'react';
import { PurchaseDateOrderSelector } from '@/components/_common/PurchaseDateOrderSelector';

interface Props {
  partId: number;
}

const LastPartsPurchasesList: React.FC<Props> = ({ partId }) => {
  const intl = useIntl();

  const [queryParams, setQueryParams] = useState<LastItemPurchasesRequest>({
    partId,
    orderByColumn: LastItemPurchasesOrderColumn.RELEASE_DATE,
  });

  const queryDebounce = useDebounce({ ...queryParams }, 500);
  const { data, isLoading } = useLastPartsPurchases(queryDebounce);

  const columns: ColumnsType<LastItemPurchases> = [
    {
      dataIndex: 'code',
      title: intl.formatMessage({ id: 'parts.lastPurchases.domain.code' }),
      ellipsis: true,
      width: 90,
    },
    {
      dataIndex: 'supplier',
      title: intl.formatMessage({ id: 'parts.lastPurchases.domain.supplier' }),
      width: 230,
    },
    {
      dataIndex: 'issueDate',
      title: intl.formatMessage({
        id: 'parts.lastPurchases.domain.issueDate',
      }),
      width: 150,
      render: (value) =>
        value ? moment(value).format('DD/MM/YYYY HH:MM') : '-',
    },
    {
      dataIndex: 'releaseDate',
      title: intl.formatMessage({
        id: 'parts.lastPurchases.domain.releaseDate',
      }),
      width: 150,
      render: (value) =>
        value ? moment(value).format('DD/MM/YYYY HH:MM') : '-',
    },
    {
      dataIndex: 'quantity',
      title: intl.formatMessage({ id: 'parts.lastPurchases.domain.quantity' }),
      width: 100,
      render: (value: number) => quantityFormatter(value),
    },
    {
      dataIndex: 'unitaryValue',
      title: intl.formatMessage({
        id: 'parts.lastPurchases.domain.unitaryValue',
      }),
      width: 100,
      render: (value: number) =>
        !hasPermissions([Authorities.REGISTER_PART_VIEW_PRICES])
          ? '-'
          : quantityFormatter(value),
    },
    {
      dataIndex: 'totalAmountItem',
      title: intl.formatMessage({
        id: 'parts.lastPurchases.domain.totalAmountItem',
      }),
      width: 100,
      render: (value: number) =>
        !hasPermissions([Authorities.REGISTER_PART_VIEW_PRICES])
          ? '-'
          : quantityFormatter(value),
    },
  ];

  return (
    <>
      <PurchaseDateOrderSelector
        defaultValue={queryParams.orderByColumn}
        onChange={(value) =>
          setQueryParams((prev) => ({ ...prev, orderByColumn: value }))
        }
      />

      <Table<LastItemPurchases>
        columns={columns}
        dataSource={data}
        loading={isLoading}
        pagination={false}
        rowKey={(row) => row.id}
        scroll={{ x: getTableScrollX(columns) }}
      />

      <Divider />

      <Card hidden={!hasPermissions([Authorities.REGISTER_PART_VIEW_PRICES])}>
        <strong>
          {intl.formatMessage({
            id: 'parts.lastPurchases.domain.averageCostLastPurchases',
          })}
          {quantityFormatter(
            data
              ? data.reduce((acc, curr) => acc + curr.totalAmountItem, 0) /
                  data.reduce((acc, curr) => acc + curr.quantity, 0)
              : 0,
          )}
        </strong>
      </Card>
    </>
  );
};

export default LastPartsPurchasesList;
